import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  LeaseDepositLedgerDto,
  AddLeaseDepositDto,
  LeaseDepositDto,
  AllocateDepositRequestDto,
  TransactionDto,
  AddLeaseDepositPaymentDto,
  ReleaseDepositRequestDto,
  AdjustLeaseDepositDto,
  AddLeaseDepositFilesDto,
  UpdateLeaseDepositFilesDto,
  RemoveLeaseDepositFilesDto,
  AdjustLeaseDepositPaymentDto,
} from '~/swagger/portal/Api';

const LEASES_URL = '/api/leases';

export class LeaseDepositRepository extends BaseRestRepository {
  public async getLeaseDeposit(leaseId: string): Promise<LeaseDepositLedgerDto> {
    validateId(leaseId);
    const url = `${LEASES_URL}/${leaseId}/deposits/ledger`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }
  public async addLeaseDeposit(leaseId: string, model: AddLeaseDepositDto): Promise<LeaseDepositDto> {
    validateId(leaseId);

    const url = `${LEASES_URL}/${leaseId}/deposits`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async allocateLeaseDeposit(leaseId: string, model: AllocateDepositRequestDto): Promise<TransactionDto> {
    validateId(leaseId);
    const url = `${LEASES_URL}/${leaseId}/deposits/allocate`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async addLeaseDepositOfflinePayment(leaseId: string, model: AddLeaseDepositPaymentDto): Promise<LeaseDepositDto> {
    validateId(leaseId);

    const url = `${LEASES_URL}/${leaseId}/deposits/payments`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async releaseDeposit(leaseId: string, request: ReleaseDepositRequestDto): Promise<LeaseDepositDto> {
    validateId(leaseId);

    const url = `${LEASES_URL}/${leaseId}/deposits/release`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.getJsonResponse(response);
  }

  public async getDepositById(leaseId: string, depositId: string): Promise<LeaseDepositDto> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    const url = `${LEASES_URL}/${leaseId}/deposits/${depositId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }

  public async adjustDeposit(leaseId: string, depositId: string, model: AdjustLeaseDepositDto): Promise<LeaseDepositDto> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    const url = `${LEASES_URL}/${leaseId}/deposits/${depositId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async deleteDepositBill(leaseId: string, depositId: string): Promise<void> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    const url = `${LEASES_URL}/${leaseId}/deposits/${depositId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async uploadDepositFiles(leaseId: string, depositId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    if (files.length === 0) return [];

    return uploadSessionRepository.uploadFiles(`${LEASES_URL}/${leaseId}/deposits/${depositId}/files/upload-sessions`, files, opts);
  }

  public async addDepositFiles(leaseId: string, depositId: string, model: AddLeaseDepositFilesDto): Promise<LeaseDepositDto> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    const url = `${LEASES_URL}/${leaseId}/deposits/${depositId}/files`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async updateDepositFiles(leaseId: string, depositId: string, model: UpdateLeaseDepositFilesDto): Promise<LeaseDepositDto> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    const url = `${LEASES_URL}/${leaseId}/deposits/${depositId}/files`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async removeDepositFiles(leaseId: string, depositId: string, model: RemoveLeaseDepositFilesDto): Promise<LeaseDepositDto> {
    validateId(leaseId, 'Lease');
    validateId(depositId, 'Deposit');
    const url = `${LEASES_URL}/${leaseId}/deposits/${depositId}/files`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async getDepositPayment(leaseId: string, paymentId: string): Promise<TransactionDto> {
    validateId(leaseId, 'Lease');
    validateId(paymentId, 'Payment');
    const url = `${LEASES_URL}/${leaseId}/deposits/payments/${paymentId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }

  public async adjustDepositPayment(leaseId: string, paymentId: string, model: AdjustLeaseDepositPaymentDto): Promise<TransactionDto> {
    validateId(leaseId, 'Lease');
    validateId(paymentId, 'Payment');
    const url = `${LEASES_URL}/${leaseId}/deposits/payments/${paymentId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(model),
    });
    return this.getJsonResponse(response);
  }

  public async deleteDepositPayment(leaseId: string, paymentId: string): Promise<void> {
    validateId(leaseId, 'Lease');
    validateId(paymentId, 'Payment');
    const url = `${LEASES_URL}/${leaseId}/deposits/payments/${paymentId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async getDepositTransfer(leaseId: string, transferId: string): Promise<TransactionDto> {
    validateId(leaseId, 'Lease');
    validateId(transferId, 'Transfer');
    const url = `${LEASES_URL}/${leaseId}/deposits/transfers/${transferId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'GET',
    });
    return this.getJsonResponse(response);
  }

  public async deleteDepositTransfer(leaseId: string, transferId: string): Promise<void> {
    validateId(leaseId, 'Lease');
    validateId(transferId, 'Transfer');
    const url = `${LEASES_URL}/${leaseId}/deposits/transfers/${transferId}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }
}
